// @flow

import * as React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';

import { createSelector } from 'reselect';

import { selectPricingInfos } from '@gandi/billing';
import { useResourceFetch } from '@gandi/resource/react';

import type { FetchParams } from '@gandi/resource/redux';

import { getDataDependency, getDisplayName } from 'gandi.react-helpers';

import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  serversResource,
  type ServerSummary,
} from '../../modules/cloud/resources/servers';
import type { FullState, PreFetchArgs, PreFetchReturn } from '../../types';

export type FetchServersParams = {
  sharing_id: string,
  per_page: number,
};

export const selectFetchServersParams: (
  state: FullState,
) => FetchServersParams = createSelector(
  [selectPricingInfos],
  pricingInfos => ({
    sharing_id: pricingInfos.sharingId,
    per_page: 1, // no more need to know if the user already has a server
  }),
);

export function useServers(): {
  fetchServersParams: FetchServersParams,
  isFailureServers: boolean,
  isFetchingServers: boolean,
  isForbiddenServers: boolean,
  isLoadedServers: boolean,
  label: string,
  shouldFetchServers: boolean,
  servers: ?Array<ServerSummary>,
} {
  const fetchServersParams = useAppSelector(selectFetchServersParams);
  const {
    summaries: servers,
    isFailure: isFailureServers,
    isFetching: isFetchingServers,
    isForbidden: isForbiddenServers,
    isLoaded: isLoadedServers,
    label,
    shouldFetch: shouldFetchServers,
  } = useResourceFetch(
    serversResource,
    // $FlowFixMe[incompatible-cast]
    (fetchServersParams: FetchParams),
  );

  return {
    fetchServersParams,
    isFailureServers,
    isFetchingServers,
    isForbiddenServers,
    isLoadedServers,
    label,
    shouldFetchServers,
    servers,
  };
}

export function preFetchServers(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  const fetchParams = selectFetchServersParams(args.getState());
  const label = serversResource.computeLabel(
    serversResource.options.fetchPath,
    // $FlowFixMe[incompatible-cast]
    (fetchParams: FetchParams),
  );
  return args.dispatch(
    serversResource.fetch(serversResource.options.fetchPath, {
      label,
      // $FlowFixMe[incompatible-call]
      params: fetchParams,
    }),
  );
}

export function withServers<C: React.ComponentType<any>>(
  WrappedComponent: C,
): any {
  function WithServers(props: React.ElementConfig<C>) {
    const serversProps = useServers();

    return <WrappedComponent {...props} {...serversProps} />;
  }

  const HoistedComponent = hoistNonReactStatics(WithServers, WrappedComponent);

  HoistedComponent.WrappedComponent = WrappedComponent;
  HoistedComponent.displayName = `WithServers(${getDisplayName(
    WrappedComponent,
  )})`;
  HoistedComponent.preFetch = getDataDependency(WrappedComponent, 'preFetch');

  return HoistedComponent;
}
